import { JAM_METADATA_MAX_BYTES, type OrganizationFeatures } from '@hub/contracts'
import type { ClientEvent } from './client-events'

/**
 * What every Jam carries (`jam.metadata()` — evaluated when a user creates a
 * Jam, see `jam-loader.tsx`). Deliberately small and deliberately free of
 * anything personal: ids, roles and versions let us find the thread or
 * document a report is about, nothing here names the reporter.
 *
 * Adding a key: keep it JSON-serializable, keep it out of
 * `FORBIDDEN_KEY_WORDS`, and extend `jam-metadata.test.ts`.
 */
export interface JamMetadata {
  appVersion: string
  buildId: string
  /** The router match id (`/$projectId/write/$docId`), never the URL. */
  routeId: string
  organizationId?: string
  projectId?: string
  projectRole?: 'owner' | 'admin' | 'member'
  uiLanguage: string
  theme: 'light' | 'dark'
  activeDocumentId?: string
  activeThreadId?: string
  /** Organization feature flags that are on. */
  enabledFeatures: string[]
  /** The newest client-side failures (`client-events.ts`), oldest first. */
  recentClientEvents: ClientEvent[]
}

/** Everything the builder reads — a snapshot the loader assembles at capture time. */
export interface JamMetadataInput {
  appVersion: string
  buildId: string
  routeId: string
  /** Route params merged over every match (`projectId`, `docId`, `chatId`, …). */
  params: Record<string, string | undefined>
  organizationId?: string
  projectRole?: JamMetadata['projectRole']
  features?: Partial<OrganizationFeatures> | null
  uiLanguage: string
  theme: JamMetadata['theme']
  events: ClientEvent[]
}

/** How many ring-buffer events a Jam carries. */
export const JAM_EVENTS_IN_METADATA = 5

export function buildJamMetadata(input: JamMetadataInput): JamMetadata {
  const features = input.features ?? {}
  const metadata: JamMetadata = {
    appVersion: input.appVersion,
    buildId: input.buildId,
    routeId: input.routeId,
    organizationId: input.organizationId,
    projectId: input.params.projectId,
    projectRole: input.params.projectId ? input.projectRole : undefined,
    uiLanguage: input.uiLanguage,
    theme: input.theme,
    activeDocumentId: input.params.docId,
    activeThreadId: input.params.chatId,
    enabledFeatures: (Object.keys(features) as Array<keyof OrganizationFeatures>)
      .filter((key) => features[key] === true)
      .sort(),
    recentClientEvents: input.events.slice(-JAM_EVENTS_IN_METADATA),
  }
  // Absent keys instead of `undefined` (which JSON.stringify drops anyway).
  for (const key of Object.keys(metadata) as Array<keyof JamMetadata>) {
    if (metadata[key] === undefined) delete metadata[key]
  }
  return metadata
}

/**
 * Key words that must never appear in the metadata — a key like `userEmail`
 * or `threadContent` is a leak by construction. Camel-case keys are split
 * into words before matching, so `routeId` passes and `routeName` would not.
 */
export const FORBIDDEN_KEY_WORDS = new Set([
  'email',
  'emails',
  'name',
  'names',
  'username',
  'token',
  'tokens',
  'content',
  'contents',
  'body',
  'text',
  'message',
  'messages',
  'password',
  'secret',
  'phone',
  'user',
  'userid',
])

function keyWords(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_-]+/)
    .map((word) => word.toLowerCase())
    .filter(Boolean)
}

/** Every key (at any depth) whose words include a forbidden one. */
export function forbiddenMetadataKeys(value: unknown, path = ''): string[] {
  if (!value || typeof value !== 'object') return []
  if (Array.isArray(value)) {
    return value.flatMap((item, i) => forbiddenMetadataKeys(item, `${path}[${i}]`))
  }
  return Object.entries(value).flatMap(([key, child]) => {
    const here = path ? `${path}.${key}` : key
    const hit = keyWords(key).some((word) => FORBIDDEN_KEY_WORDS.has(word)) ? [here] : []
    return [...hit, ...forbiddenMetadataKeys(child, here)]
  })
}

/** Serialized size in bytes (what Jam measures against its 10 kB limit). */
export function jamMetadataBytes(metadata: JamMetadata): number {
  return new TextEncoder().encode(JSON.stringify(metadata)).length
}

/**
 * Jam refuses metadata over 10 kB. The ids are bounded, so this only ever
 * trips on a pathological ring buffer — drop the events rather than the Jam.
 */
export function fitJamMetadata(metadata: JamMetadata): JamMetadata {
  if (jamMetadataBytes(metadata) <= JAM_METADATA_MAX_BYTES) return metadata
  return { ...metadata, recentClientEvents: [] }
}
